import React, { createRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { FormattedMessage, FormattedDate } from 'react-intl';
import { Button } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';

import BookDetails from 'Features/Product/components/ProductAboutSection/BookDetails';
import DangerousHTML from 'Components/Utility/DangerousHTML';
import FluidImg from 'Components/Utility/FluidImg';
import GiftCardHowToSteps from 'Features/Product/components/ProductAboutSection/GiftCardHowToSteps';
import PreorderBonus from 'Components/ProductOffers/PreorderBonus';
import ProductDetails from 'Features/Product/components/ProductAboutSection/ProductDetails';
import ProductRequirements from 'Helpers/Product/ProductRequirements';
import SampleDownloads from 'Components/Utility/Sample/SampleDownloads';
import UtalkFeatures from 'Features/Product/components/Utalk/UtalkFeatures';
import useViewport from 'Hooks/useViewport';

const AboutTab = ({
  currentDrm,
  product,
  downloadTypes,
  sampleDownload,
}) => {
  const aboutTabContentRef = createRef();
  const aboutTabDescriptionRef = createRef();

  const description = product.desc || '';
  const requirements = ProductRequirements.ShowRequirements(product);
  const dates = ProductRequirements.ReleaseDates(product);
  const nintendoProduct = ProductRequirements.ShowNintendo(product);
  const preorderDelivery = get(product, 'preorder_delivery', false);
  const preorderKeyTime = get(product, 'preorderKeyTime', '');
  const preorderPlayableFrom = get(product, 'preorderPlayableFrom', '');
  let showDeliveredOnPurchaseMessage = true;
  if (Date.now() < Date.parse(preorderKeyTime) || Date.now() < Date.parse(preorderPlayableFrom)) {
    showDeliveredOnPurchaseMessage = false;
  }

  const [showMoreDetails, setShowMoreDetails] = useState(true);
  const [hideButton, setHideButton] = useState(false);

  const expandAboutTab = () => {
    aboutTabContentRef.current.style.maxHeight = '100%';
    setHideButton(true);
  };

  const { isXxl, isXl, isLg, isMd, isSm, isXs } = useViewport();

  const showPreorderBonus =
    (product.preorderCover && dates.isPreorder) ||
    (dates.earlyAdopterBonus && product.preorderCover);

  let maxHeight = 500;
  if (!showPreorderBonus && isXl) maxHeight = 580;
  if (!showPreorderBonus && isXxl) maxHeight = 712;
  if (showPreorderBonus && isXs) maxHeight = 600;
  if (showPreorderBonus && isSm) maxHeight = 700;
  if (showPreorderBonus && isMd) maxHeight = 800;
  if (showPreorderBonus && isLg) maxHeight = 780;
  if (showPreorderBonus && isXl) maxHeight = 840;
  if (showPreorderBonus && isXxl) maxHeight = 920;

  // Check that the height of the description is less than the maxHeight. If so, hide the more details button
  useEffect(() => {
    const aboutTabDescriptionHeight = aboutTabDescriptionRef.current.getBoundingClientRect().height;
    if (aboutTabDescriptionHeight !== 0 && aboutTabDescriptionHeight < maxHeight) {
      setShowMoreDetails(false);
    }
  }, [maxHeight, aboutTabDescriptionRef]);

  let productType = product.type;
  if (product.type === 'dlc') productType = 'DLC';
  if (currentDrm === 'voucher' || currentDrm === 'redeem') productType = 'product';
  if (product.slug === 'rewards-week-2023-sign-in-and-win-contest') productType = 'contest';
  if (product.display_type === 'graphic-novel') productType = 'Graphic Novel';
  if (product.display_type === 'audio') productType = 'Audio';
  if (product.type === 'book' && product.display_type === 'audio') productType = 'Audiobook';

  return (
    <React.Fragment>
      <div className={classNames('about-tab-content')} style={{ maxHeight }} ref={aboutTabContentRef}>
        <article className="about-tab-description-container" id="description-container" ref={aboutTabDescriptionRef}>
          <h3 className="section-heading">
            {currentDrm !== 'utalk' && currentDrm !== 'zenva' && product.display_type !== 'software' && product.display_type !== 'gift-card' &&
              <FormattedMessage
                id="product.abouttab.title"
                defaultMessage="About this {type}"
                values={{ type: productType }}
              />
            }

            {(currentDrm === 'utalk' || currentDrm === 'zenva' || product.display_type === 'software' || product.display_type === 'gift-card') &&
              <FormattedMessage id="product.abouttab.title.utalk" defaultMessage="About this product" />
            }
          </h3>

          {!isEmpty(downloadTypes) && product.slug !== 'rewards-week-2023-sign-in-and-win-contest' &&
            <div className="downloads-container">
              {downloadTypes.sort().map(file =>
                <div key={file} className="comic-download mr-2">
                  {file}
                </div>,
              )}
            </div>
          }

          {showPreorderBonus &&
            <PreorderBonus
              preorderGraphic
              product={product}
            />
          }

          {(
            (product.preorder_info && dates.isPreorder) ||
            (product.preorder_info && dates.earlyAdopterBonus)) &&
            <div className="pre-purchase-container">
              <h3 className="section-heading">
                {dates.earlyAdopterBonus ?
                  <FormattedMessage
                    id="product.abouttab.earlyadopters"
                    defaultMessage="Early Adopter Bonus"
                  /> :
                  <FormattedMessage
                    id="product.abouttab.prepurchase"
                    defaultMessage="Pre-purchase Information"
                  />
                }
              </h3>

              {dates.prerelease && !product.no_release_date && product.slug !== 'hogwarts-legacy-digital-deluxe-edition' &&
                <div className="pre-purchase-delivery-message">
                  {(preorderDelivery || product.ztormId) &&
                    <FormattedMessage
                      id="product.abouttab.delivered-in-future"
                      defaultMessage="The product will be delivered on or before {releaseDate}."
                      values={{
                        releaseDate: dates.hideDate ?
                          <FormattedDate
                            value={dates.earlyAccess ? dates.playableFrom : dates.releaseDate}
                            year="numeric"
                          />
                          :
                          <FormattedDate
                            value={dates.earlyAccess ? dates.playableFrom : dates.releaseDate}
                            year="numeric"
                            month="long"
                            day="2-digit"
                          />,
                      }}
                    />
                  }
                  {!preorderDelivery && !product.ztormId && showDeliveredOnPurchaseMessage &&
                    <FormattedMessage
                      id="product.abouttab.delivered-on-purchase"
                      defaultMessage="The product will be delivered on purchase."
                    />
                  }
                </div>
              }

              <DangerousHTML html={product.preorder_info === null ? '' : product.preorder_info} />
            </div>
          }

          {product.deluxeCover &&
            <div className="responsive-image responsive-image--16by9 w-100 mb-3">
              <FluidImg
                src={product.deluxeCover}
                alt="Pre-order Game Cover"
                sizesOverride={'(min-width: 1520px) 747px, (min-width: 1200px) 608px, (min-width: 992px) 528px, (min-width: 768px) 640px,  (min-width: 576px) 476px, 98vw'}
              />
            </div>
          }

          <div className="product-desc-container">
            <DangerousHTML tag="p" html={description} />
          </div>

          {currentDrm === 'utalk' &&
            <React.Fragment>
              <UtalkFeatures />
            </React.Fragment>
          }
        </article>

        <aside className="about-tab-product-details-container" id="details-container">
          {sampleDownload &&
            <article>
              <h3 className="section-heading">
                <FormattedMessage
                  id="product.abouttab.preview.title"
                  defaultMessage="Preview {type}"
                  values={{ type: productType }}
                />
              </h3>
              <SampleDownloads download={sampleDownload} />
              <hr />
            </article>
          }

          {(product.display_type !== 'book' && product.display_type !== 'graphic-novel' && product.display_type !== 'comic' && product.display_type !== 'gift-card') &&
            <ProductDetails
              soldOut={product.sold_out}
              data={{
                publishers: product.publishers,
                developers: product.developers,
                genres: product.genres,
                collections: product.collections,
                features: product.features,
                combo: product.combo,
                themes: product.themes,
                playstyles: product.playstyles,
                categories: product.categories,
                franchises: product.franchises,
                systems: requirements.validSystems,
                platforms: requirements.validPlatforms,
                catalina: product.catalina,
                name: product.name,
                type: product.type,
                slug: product.slug,
                age: product.age,
                languages: product.lang,
                display_type: product.display_type,
                steam: product.steam,
                parent_slug: product.parent_slug,
                userTags: product.userTags,
                xboxPlatforms: product.xbox_platforms,
              }}
              orderDate={product.order_date}
              noReleaseDate={product.no_release_date}
              released={dates.releaseDate}
              earlyAccess={dates.earlyAccess}
              playableFrom={dates.playableFrom}
              links={{ url: product.url, steam: product.steam }}
              nintendoProduct={nintendoProduct}
              currentDrm={currentDrm}
              giveaway={product.giveaway}
              vrSupport={product.vr_support}
            />
          }

          {product.display_type === 'gift-card' &&
            <GiftCardHowToSteps
              externalRedeemLink={product.external_redeem_link}
              redemptionSteps={product.redeem_instruction}
              drm={currentDrm}
            />
          }

          {(product.display_type === 'book' || product.display_type === 'graphic-novel' || product.display_type === 'comic') && product.slug !== 'rewards-week-2023-sign-in-and-win-contest' &&
            <BookDetails
              data={{
                age: product.age,
                artists: product.artists,
                authors: product.authors,
                files: product.files,
                isbn: product.isbn,
                languages: product.lang,
                pages: product.pages,
                publishers: product.publishers,
                collections: product.collections,
              }}
              released={dates.releaseDate}
              links={{ url: product.url, steam: product.steam }}
              currentDrm={currentDrm}
              downloadTypes={downloadTypes}
              type={product.display_type}
            />
          }
        </aside>

        {showMoreDetails && !hideButton &&
          <div className="about-fade" />
        }
      </div>

      {!hideButton && showMoreDetails &&
        <div className="d-flex pt-3 more-btn-container">
          <Button color="link" className="more-btn" onClick={() => expandAboutTab()}>
            <FormattedMessage id="product.abouttab.showmore" defaultMessage="Show more" />
            <FontAwesomeIcon icon={faChevronDown} className="ml-2" />
          </Button>
        </div>
      }
    </React.Fragment>
  );
};

AboutTab.propTypes = {
  currentDrm: PropTypes.string,
  product: PropTypes.shape({}),
  sampleDownload: PropTypes.bool,
  downloadTypes: PropTypes.arrayOf(PropTypes.string),
};

AboutTab.defaultProps = {
  currentDrm: '',
  product: {},
  sampleDownload: false,
  downloadTypes: [],
};

export default AboutTab;
